/**
 * Refer-a-friend (no benefits, simple share — Handover-3 user spec).
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Copy, Share2, Users } from 'lucide-react';

export default function RefersPage() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/referrals/my-code'),
      api.get('/api/referrals/my-stats'),
    ]).then(([c, s]) => {
      setData(c.data);
      setStats(s.data);
    }).catch(() => {});
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const share = async () => {
    if (!navigator.share) {
      copy(data.share_message, 'Message');
      return;
    }
    try {
      await navigator.share({
        title: 'Soul Sanskar Academy',
        text: data.share_message,
        url: data.share_url,
      });
    } catch {}
  };

  if (!data) return null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="refers-page">
      <header>
        <h1 className="font-heading text-3xl">Refer a friend</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Share Soul Sanskar with mums-to-be in your circle. We'll let them know you sent them.
        </p>
      </header>

      <Card className="bg-gradient-to-br from-rose-50 to-amber-50 border-rose-100" data-testid="refer-link-card">
        <CardHeader>
          <CardTitle className="text-lg">Your share link</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white rounded-lg p-3 flex items-center gap-2 border">
            <code className="flex-1 truncate text-sm">{data.share_url}</code>
            <Button variant="ghost" size="sm" onClick={() => copy(data.share_url, 'Link')} data-testid="copy-share-link"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Code: <code className="font-mono">{data.code}</code></span>
            <Button onClick={share} data-testid="share-button">
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="refer-stats-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" /> Your impact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Stat label="Referred" value={stats?.totals?.referred ?? 0} />
            <Stat label="Trial paid" value={stats?.totals?.trial_paid ?? 0} />
            <Stat label="Joined Elite" value={stats?.totals?.elite ?? 0} />
          </div>
          <Separator />
          {(stats?.items || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No referrals yet — share your link!</p>
          ) : (
            <div className="mt-3 space-y-2">
              {stats.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                  <span>{it.name || 'Friend'}</span>
                  <Badge variant="secondary">{(it.learner_type || 'free').replace('_', ' ')}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-lg p-4 text-center border">
      <p className="text-2xl font-heading font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{label}</p>
    </div>
  );
}
